import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  User, Info, Award, Briefcase, Users, Globe, Plus, Mail, MapPin, Trophy, Star,
  Github, ExternalLink, ImagePlus, Trash2, FileText, Edit3, X, Save, Home, LogOut
} from "lucide-react";
import useEmojiReaction from '../../utils/useEmojiReactions';
import { useTheme } from '../../hooks/useTheme';
import API_URL from '../../config';
import LoadingScreen from '../../components/LoadingScreen';
import '../Profile.css';

// ---------------------- Helper Utilities ----------------------
const safeJsonParse = (val) => {
  if (Array.isArray(val)) return val;
  if (!val) return [];
  try {
    const parsed = JSON.parse(val);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const formatNameUpper = (value) => {
  if (!value) return "";
  return String(value).trim().replace(/\s+/g, " ").toUpperCase();
};

const deriveFromEmail = (email) => {
  if (!email) return { derivedName: "", derivedRoll: "" };
  const local = String(email).split("@")[0] || "";
  const looksLikeRoll = /\d/.test(local);
  let derivedName = "";
  let derivedRoll = "";
  if (looksLikeRoll && /^[A-Za-z0-9]+$/.test(local)) {
    derivedRoll = local;
  } else {
    const spaced = local.replace(/[._-]+/g, " ").split(" ").filter(Boolean).join(" ");
    derivedName = formatNameUpper(spaced || local);
  }
  return { derivedName, derivedRoll };
};

// ---------------------- Animation Variants ----------------------
const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.1 } } };
const itemVariants = { hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1, transition: { type: "spring", stiffness: 100 } } };
const cardContainerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.3, delayChildren: 0.2 } } };
const cardEntranceVariants = { hidden: { y: 30, opacity: 0, scale: 0.95 }, visible: { y: 0, opacity: 1, scale: 1, transition: { type: "spring", stiffness: 80, damping: 15, staggerChildren: 0.1, delayChildren: 0.1 } } };
const elementEntranceVariants = { hidden: { y: 10, opacity: 0 }, visible: { y: 0, opacity: 1, transition: { duration: 0.4, ease: "easeOut" } } };
const profilePicVariants = { hidden: { scale: 0.5, opacity: 0 }, visible: { scale: 1, opacity: 1, transition: { type: "spring", stiffness: 150, damping: 12 } } };
const profileNameVariants = { hidden: { y: 10, opacity: 0 }, visible: { y: 0, opacity: 1, transition: { duration: 0.5 } } };
const headerVariants = { hidden: { y: -50, opacity: 0 }, visible: { y: 0, opacity: 1, transition: { type: "spring", stiffness: 120, damping: 20 } } };
const tabsVariants = { hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1, transition: { type: "spring", stiffness: 100, damping: 15, delay: 0.3 } } };
const pageEntranceVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.2, delayChildren: 0.1 } } };

// ---------------------- Profile Component ----------------------
function FacultyProfile() {
  const [profile, setProfile] = useState({
    name: "", email: "", emp_id: "", department: "", gender: "", contact: "", city: "",
    linkedin: "", linkedinUrl: "", bio: "", aspirations: "", motto: "", achievements: "",
    languages: "", strengths: "", technicalCertificates: [], nonTechnicalCertificates: [],
    interestsTechnical: "", interestsNonTechnical: "", research_papers: [], github: "",
    google_scholar: "", projects: [], experience: [], clubs: [], profile_pic: "",
    work_dept: "", room_no: "", designation: "", role: "faculty"
  });

  const [connectionStatus, setConnectionStatus] = useState({ sent: "none", received: "none" });
  const [selectedTab, setSelectedTab] = useState("personal");
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(true);

  // New item states
  const [newLanguage, setNewLanguage] = useState("");
  const [newStrength, setNewStrength] = useState("");
  const [newTechnicalCertificate, setNewTechnicalCertificate] = useState("");
  const [newNonTechnicalCertificate, setNewNonTechnicalCertificate] = useState("");
  const [newPaper, setNewPaper] = useState({ title: "", url: "" });
  const [newProject, setNewProject] = useState({ name: "", description: "", techStack: "", liveLink: "", repoLink: "" });
  const [newExperience, setNewExperience] = useState({ companyName: "", duration: "", role: "", description: "", type: "Full-time" });
  const [newClub, setNewClub] = useState({ clubName: "", role: "", events: "" });

  const [pendingProfilePic, setPendingProfilePic] = useState(null);
  const [isPhotoBusy, setIsPhotoBusy] = useState(false);
  const fileInputRef = useRef(null);

  const [connectionData, setConnectionData] = useState({ following: [], followers: [] });
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  useEffect(() => {
    if (selectedTab === 'connections') {
      fetch(`${API_URL}/connections/status?email=${profile.email}`)
        .then(res => res.json())
        .then(data => setConnectionData(data))
        .catch(err => console.error("Fetch connections error", err));
    }
  }, [selectedTab, profile.email]);

  const DEPARTMENTS = ["CSE", "CSE-AIML&IOT", "AIDS", "CSBS", "IT", "ECE", "EEE", "EIE", "MECH", "CIVIL", "BIOTECH", "DS", "CYS", "R&AI"];

  const storedUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("vjconnect_user") || "{}"); } catch { return {}; }
  }, []);

  const location = useLocation();
  const navigate = useNavigate();
  const { rollNo } = useParams(); // Using rollNo param for any profile routing backward compatibility

  const requestedEmail = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("email");
  }, [location.search]);

  const targetEmail = requestedEmail || (rollNo ? (rollNo.includes('@') ? rollNo : rollNo + "@vnrvjiet.in") : storedUser?.email || "");
  const isViewingOther = targetEmail && storedUser?.email && targetEmail !== storedUser.email;

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        if (!targetEmail) { setLoading(false); return; }
        const res = await axios.get(`${API_URL}/student/profile?email=${targetEmail}&requesterEmail=${storedUser?.email || ""}&fetchAsFaculty=true`);
        if (res.data.exists) {
          const raw = res.data.profile;
          let profileName = raw.name || "";
          if (!profileName) {
            const emailParts = targetEmail.split('@')[0];
            const spaced = emailParts.split(/[._-]/).filter(Boolean).join(' ');
            profileName = formatNameUpper(spaced || emailParts);
          }
          setProfile(prev => ({
            ...prev, ...raw,
            name: formatNameUpper(profileName),
            technicalCertificates: safeJsonParse(raw.technicalCertificates),
            nonTechnicalCertificates: safeJsonParse(raw.nonTechnicalCertificates),
            research_papers: safeJsonParse(raw.research_papers),
            projects: safeJsonParse(raw.projects),
            experience: safeJsonParse(raw.experience),
            clubs: safeJsonParse(raw.clubs),
          }));
          if (isViewingOther) {
            setConnectionStatus({ sent: res.data.sentStatus || "none", received: res.data.receivedStatus || "none" });
          }
          setIsEditMode(false);
        } else {
          if (isViewingOther) {
            alert("This faculty hasn't shared their profile yet.");
            navigate(-1);
          } else {
            setProfile(p => ({ ...p, email: storedUser?.email || p.email, name: formatNameUpper(storedUser?.name || "") }));
            setIsEditMode(true);
          }
        }
      } catch (error) { console.error("Error fetching profile:", error); } finally { setLoading(false); }
    };
    fetchProfile();
  }, [targetEmail, isViewingOther, navigate, storedUser]);

  const handleChange = (e) => setProfile(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const parseList = (str) => String(str || "").split(',').map(s => s.trim()).filter(Boolean);

  // List Add/Remove handlers
  const addToList = (field, value, setter) => {
    const candidate = String(value || "").trim();
    if (!candidate) return;
    const list = parseList(profile[field]);
    if (list.includes(candidate)) { setter(""); return; }
    setProfile(p => ({ ...p, [field]: [...list, candidate].join(', ') }));
    setter("");
  };
  const removeFromList = (field, idx) => {
    const list = parseList(profile[field]);
    list.splice(idx, 1);
    setProfile(p => ({ ...p, [field]: list.join(', ') }));
  };

  const addArrayItem = (field, item, resetter, defaultItem) => {
    setProfile(p => ({ ...p, [field]: [...(p[field] || []), item] }));
    resetter(defaultItem);
  };
  const removeArrayItem = (field, idx) => setProfile(p => ({ ...p, [field]: (p[field] || []).filter((_, i) => i !== idx) }));

  // Connections
  const handleRemoveConnection = async (tEmail) => {
    try {
      const res = await axios.post(`${API_URL}/connect/remove`, { userEmail: storedUser.email, targetEmail: tEmail });
      if (res.data.success) {
        setConnectionData(prev => ({ following: prev.following.filter(f => f.email !== tEmail), followers: prev.followers.filter(f => f.email !== tEmail) }));
        if (isViewingOther && tEmail === profile.email) setConnectionStatus({ sent: "none", received: "none" });
      }
    } catch (err) { console.error(err); }
  };
  const handleConnect = async () => {
    try {
      const res = await axios.post(`${API_URL}/connect/request`, { senderEmail: storedUser.email, senderName: storedUser.name, recipientEmail: profile.email });
      if (res.data.success) { setConnectionStatus(prev => ({ ...prev, sent: "pending" })); alert("Connection request sent!"); }
    } catch (err) { console.error(err); }
  };
  const handleAcceptConnect = async (tEmail = null) => {
    const requesterEmail = tEmail || profile.email;
    try {
      const res = await axios.post(`${API_URL}/connect/accept`, { accepterEmail: storedUser.email, accepterName: storedUser.name, requesterEmail });
      if (res.data.success) {
        if (!tEmail) { setConnectionStatus(prev => ({ ...prev, received: "accepted" })); window.location.reload(); }
        else { fetch(`${API_URL}/connections/status?email=${profile.email}`).then(r => r.json()).then(d => setConnectionData(d)); }
      }
    } catch (err) { console.error(err); }
  };

  // Picture Upload
  const handleProfilePicSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) return alert("Please choose an image <= 1 MB.");
    const reader = new FileReader();
    reader.onload = () => setPendingProfilePic({ preview: reader.result, dataUrl: reader.result, fileSize: file.size });
    reader.readAsDataURL(file);
    event.target.value = "";
  };

  const uploadProfilePic = async () => {
    if (!pendingProfilePic?.dataUrl || !profile.email) return;
    setIsPhotoBusy(true);
    try {
      const res = await axios.post(`${API_URL}/upload_profile_pic`, { email: profile.email, imageData: pendingProfilePic.dataUrl });
      setProfile(p => ({ ...p, profile_pic: res.data.profile_pic }));
      setPendingProfilePic(null);
    } catch (e) { alert("Failed to upload profile picture."); }
    setIsPhotoBusy(false);
  };

  const removeProfilePic = async () => {
    if (!profile.email) return;
    setIsPhotoBusy(true);
    try {
      await axios.post(`${API_URL}/remove_profile_pic`, { email: profile.email });
      setProfile(p => ({ ...p, profile_pic: "" }));
      setPendingProfilePic(null);
    } catch (e) { alert("Failed to remove profile picture."); }
    setIsPhotoBusy(false);
  };

  const handleSave = async () => {
    if (isViewingOther) return;
    setIsPhotoBusy(true); // Reusing as general loading state for save
    try {
      let effectiveProfilePic = profile.profile_pic || "";
      if (pendingProfilePic && pendingProfilePic.dataUrl) {
        const uploadResp = await axios.post(`${API_URL}/upload_profile_pic`, { email: profile.email, imageData: pendingProfilePic.dataUrl });
        effectiveProfilePic = uploadResp.data.profile_pic;
        setProfile((prev) => ({ ...prev, profile_pic: effectiveProfilePic }));
        setPendingProfilePic(null);
      }

      if (profile.contact && profile.contact.trim().length > 0 && profile.contact.trim().length !== 10) {
        alert("Contact number must be exactly 10 digits."); setIsPhotoBusy(false); return;
      }

      const payload = { ...profile, profile_pic: effectiveProfilePic, saveAsFaculty: true };
      const requiredFields = ['email', 'name', 'work_dept', 'gender', 'emp_id'];
      for (const field of requiredFields) {
        if (!payload[field]) { alert(`Please fill in all required fields. Missing: ${field}`); setIsPhotoBusy(false); return; }
      }

      const res = await axios.post(`${API_URL}/student/profile/update`, payload);
      if (res.data.success) {
        alert("Profile saved successfully!");
        setIsEditMode(false);
      } else { alert("Failed to save profile."); }
    } catch (error) {
      console.error(error); alert("Failed to save profile. Please try again.");
    } finally { setIsPhotoBusy(false); }
  };

  if (loading) return <LoadingScreen message="Loading Faculty Profile" />;

  const tabs = [
    { id: "personal", label: "Personal", icon: User },
    { id: "about", label: "About", icon: Info },
    { id: "skills", label: "Skills", icon: Award },
    { id: "research", label: "Research Profiles", icon: FileText },
    { id: "work", label: "Work", icon: Briefcase },
    { id: "clubs", label: "Clubs", icon: Globe },
    { id: "connections", label: "Network", icon: Users },
  ];

  const getJoinYear = (empId) => empId ? `20${empId.substring(0, 2)}` : "N/A";

  const renderTabContent = () => {
    switch (selectedTab) {
      case "personal":
        return (
          <motion.div variants={cardContainerVariants} initial="hidden" animate="visible" exit={{ opacity: 0, y: -20 }} className="profile-container">
            <motion.div variants={cardEntranceVariants} className="profile-avatar-section">
              <motion.div variants={profilePicVariants} className={`profile-avatar-wrap ${isEditMode ? 'cursor-pointer' : 'cursor-zoom-in'}`} onClick={() => !isEditMode && setIsImageModalOpen(true)}>
                <div className="profile-avatar-inner">
                  {(pendingProfilePic?.preview || profile.profile_pic) ? (
                    <img src={pendingProfilePic?.preview || profile.profile_pic} alt="Profile" />
                  ) : (
                    <span className="profile-avatar-initial">{(profile.name || '?').charAt(0).toUpperCase()}</span>
                  )}
                </div>
              </motion.div>
              {!isEditMode && (
                <motion.div variants={profileNameVariants} className="text-center">
                  <h2 className="profile-name-display gradient-text">{profile.name}</h2>
                  <div className="profile-info-display mt-2">
                    <span className="profile-info-item"><Mail /> {profile.email}</span>
                    {profile.city && <span className="profile-info-item"><MapPin /> {profile.city}</span>}
                  </div>
                  {isViewingOther && (
                    <div className="mt-4">
                      {connectionStatus.sent === 'accepted' && connectionStatus.received === 'accepted' ? (
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                          <button disabled className="btn-profile btn-profile-secondary">Mutual Connection 🤝</button>
                          <button onClick={() => handleRemoveConnection(profile.email)} className="btn-profile btn-profile-danger" style={{ padding: '8px 12px' }}><Trash2 size={16} /></button>
                        </div>
                      ) : connectionStatus.sent === 'accepted' ? (
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                          <button disabled className="btn-profile btn-profile-secondary">Connected ✅</button>
                          <button onClick={() => handleRemoveConnection(profile.email)} className="btn-profile btn-profile-danger" style={{ padding: '8px 12px' }}><Trash2 size={16} /></button>
                        </div>
                      ) : connectionStatus.sent === 'pending' ? (
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                          <button disabled className="btn-profile btn-profile-secondary">Request Sent ⏳</button>
                          <button onClick={() => handleRemoveConnection(profile.email)} className="btn-profile btn-profile-danger" style={{ padding: '8px 12px' }}>Cancel</button>
                        </div>
                      ) : connectionStatus.received === 'accepted' ? (
                        <button onClick={handleConnect} className="btn-profile btn-profile-primary">Connect Back 🔗</button>
                      ) : connectionStatus.received === 'pending' ? (
                        <div style={{ display: 'flex', gap: 10 }}>
                          <button onClick={handleAcceptConnect} className="btn-profile btn-profile-primary">Accept Request ✅</button>
                          <button onClick={() => handleRemoveConnection(profile.email)} className="btn-profile btn-profile-danger">Reject</button>
                        </div>
                      ) : (
                        <button onClick={handleConnect} className="btn-profile btn-profile-primary">Connect 🔗</button>
                      )}
                    </div>
                  )}
                </motion.div>
              )}
              {isEditMode && (
                <motion.div variants={elementEntranceVariants} className="profile-avatar-actions">
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleProfilePicSelect} style={{ display: 'none' }} />
                  <button type="button" onClick={() => fileInputRef.current?.click()} disabled={isPhotoBusy} className="btn-profile btn-profile-secondary"><ImagePlus size={16} /> Choose</button>
                  <button type="button" onClick={uploadProfilePic} disabled={isPhotoBusy || !pendingProfilePic} className="btn-profile btn-profile-primary">Upload</button>
                  <button type="button" onClick={removeProfilePic} disabled={isPhotoBusy || (!profile.profile_pic && !pendingProfilePic)} className="btn-profile btn-profile-danger"><Trash2 size={16} /> Remove</button>
                </motion.div>
              )}
            </motion.div>

            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header"><User /> Basic Information</h3>
              <div className="profile-form-grid">
                <div className="profile-form-row"><label className="profile-label">Full Name{isEditMode && " *"}</label><input type="text" name="name" value={profile.name} onChange={handleChange} disabled={!isEditMode} className="profile-input" /></div>
                <div className="profile-form-row"><label className="profile-label">Email{isEditMode && " *"}</label><input type="email" value={profile.email} disabled className="profile-input" /></div>
                <div className="profile-form-row"><label className="profile-label">Employee ID{isEditMode && " *"}</label><input type="text" name="emp_id" value={profile.emp_id || ""} onChange={handleChange} disabled={!isEditMode} className="profile-input" /></div>
                <div className="profile-form-row"><label className="profile-label">Room Number</label><input type="text" name="room_no" value={profile.room_no || ""} onChange={handleChange} disabled={!isEditMode} className="profile-input" /></div>
                <div className="profile-form-row"><label className="profile-label">Year of Joining</label><div className="profile-input" style={{ opacity: 0.7 }}>{getJoinYear(profile.emp_id)}</div></div>
                <div className="profile-form-row"><label className="profile-label">Gender{isEditMode && " *"}</label><select name="gender" value={profile.gender || ""} onChange={handleChange} disabled={!isEditMode} className="profile-select"><option value="">Select</option><option value="Male">Male</option><option value="Female">Female</option></select></div>
                <div className="profile-form-row"><label className="profile-label">Department{isEditMode && " *"}</label><select name="work_dept" value={profile.work_dept || ""} onChange={handleChange} disabled={!isEditMode} className="profile-select"><option value="">Select</option>{DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}</select></div>
                <div className="profile-form-row">
                  <label className="profile-label">Contact Number</label>
                  {isEditMode ? (
                    <input
                      type="text"
                      name="contact"
                      value={profile.contact || ""}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                        handleChange({ target: { name: 'contact', value: val } });
                      }}
                      className="profile-input"
                      placeholder="10-digit mobile number"
                      maxLength={10}
                    />
                  ) : (
                    <div className="flex gap-2 items-center" style={{ width: '100%' }}>
                      {profile.contact ? (
                        <>
                          <span className="profile-input" style={{ flex: 1 }}>{profile.contact}</span>
                          <a
                            href={`tel:${profile.contact}`}
                            className="btn-profile btn-profile-secondary flex items-center justify-center"
                            style={{ textDecoration: 'none', display: 'inline-flex', padding: '10px 14px' }}
                          >
                            📞
                          </a>
                        </>
                      ) : (
                        <span className="profile-input text-gray-400">Mutual Connections Only</span>
                      )}
                    </div>
                  )}
                </div>
                <div className="profile-form-row"><label className="profile-label">City/Location</label><input type="text" name="city" value={profile.city} onChange={handleChange} disabled={!isEditMode} className="profile-input" /></div>
              </div>
            </motion.div>

            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header"><ExternalLink /> Social & Portfolio</h3>
              <div className="profile-form-row full-width">
                <label className="profile-label">LinkedIn</label>
                {isEditMode ? (
                  <div className="flex flex-col gap-2">
                    <input type="text" name="linkedin" value={profile.linkedin} onChange={handleChange} className="profile-input" placeholder="Username" />
                    <input type="text" name="linkedinUrl" value={profile.linkedinUrl} onChange={handleChange} className="profile-input" placeholder="Full URL" />
                  </div>
                ) : (
                  <div className="flex gap-2 items-center">
                    <span className="profile-input flex-1">{profile.linkedin || "Not provided"}</span>
                    {profile.linkedinUrl && <a href={profile.linkedinUrl} target="_blank" rel="noreferrer" className="btn-profile btn-profile-secondary"><ExternalLink size={16} /></a>}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        );

      case "about":
        return (
          <motion.div variants={cardContainerVariants} initial="hidden" animate="visible" exit={{ opacity: 0, y: -20 }} className="profile-container">
            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header"><Info /> Bio & Personality</h3>
              <textarea name="bio" value={profile.bio} onChange={handleChange} disabled={!isEditMode} className="profile-textarea profile-textarea-scrollable" rows={4} />
            </motion.div>
            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header"><Star /> Aspirations</h3>
              <textarea name="aspirations" value={profile.aspirations} onChange={handleChange} disabled={!isEditMode} className="profile-textarea profile-textarea-scrollable" rows={4} />
            </motion.div>
            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header"><Trophy /> Motto</h3>
              <input type="text" name="motto" value={profile.motto} onChange={handleChange} disabled={!isEditMode} className="profile-input" />
            </motion.div>
            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header"><Award /> Achievements</h3>
              <textarea name="achievements" value={profile.achievements} onChange={handleChange} disabled={!isEditMode} className="profile-textarea" />
            </motion.div>
            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header"><Globe /> Languages & Strengths</h3>
              <div className="profile-form-row mb-4">
                <label className="profile-label">Languages</label>
                <div className="profile-tags">
                  {parseList(profile.languages).map((idx, i) => <span key={i} className="profile-tag profile-tag-tech">{idx} {isEditMode && <button onClick={() => removeFromList("languages", i)} className="profile-tag-remove">×</button>}</span>)}
                </div>
                {isEditMode && <div className="profile-add-row mt-2"><input type="text" value={newLanguage} onChange={e => setNewLanguage(e.target.value)} className="profile-input" /><button onClick={() => addToList("languages", newLanguage, setNewLanguage)} className="btn-profile btn-profile-secondary"><Plus size={16} /></button></div>}
              </div>
              <div className="profile-form-row">
                <label className="profile-label">Strengths</label>
                <div className="profile-tags">
                  {parseList(profile.strengths).map((idx, i) => <span key={i} className="profile-tag profile-tag-tech">{idx} {isEditMode && <button onClick={() => removeFromList("strengths", i)} className="profile-tag-remove">×</button>}</span>)}
                </div>
                {isEditMode && <div className="profile-add-row mt-2"><input type="text" value={newStrength} onChange={e => setNewStrength(e.target.value)} className="profile-input" /><button onClick={() => addToList("strengths", newStrength, setNewStrength)} className="btn-profile btn-profile-secondary"><Plus size={16} /></button></div>}
              </div>
            </motion.div>
          </motion.div>
        );

      case "skills":
        return (
          <motion.div variants={cardContainerVariants} initial="hidden" animate="visible" exit={{ opacity: 0, y: -20 }} className="profile-container">
            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header"><Award /> Technical Certifications</h3>
              <div className="profile-list">
                {profile.technicalCertificates.map((cert, idx) => (
                  <div key={idx} className="profile-list-item flex justify-between">
                    <span>{cert}</span>{isEditMode && <button onClick={() => removeArrayItem("technicalCertificates", idx)} className="text-red-500"><Trash2 size={16} /></button>}
                  </div>
                ))}
                {isEditMode && <div className="profile-add-row mt-2"><input type="text" value={newTechnicalCertificate} onChange={e => setNewTechnicalCertificate(e.target.value)} className="profile-input" /><button onClick={() => addArrayItem("technicalCertificates", newTechnicalCertificate, setNewTechnicalCertificate, "")} className="btn-profile btn-profile-accent">Add</button></div>}
              </div>
            </motion.div>
            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header"><Award /> Non-Technical Certifications</h3>
              <div className="profile-list">
                {profile.nonTechnicalCertificates.map((cert, idx) => (
                  <div key={idx} className="profile-list-item flex justify-between">
                    <span>{cert}</span>{isEditMode && <button onClick={() => removeArrayItem("nonTechnicalCertificates", idx)} className="text-red-500"><Trash2 size={16} /></button>}
                  </div>
                ))}
                {isEditMode && <div className="profile-add-row mt-2"><input type="text" value={newNonTechnicalCertificate} onChange={e => setNewNonTechnicalCertificate(e.target.value)} className="profile-input" /><button onClick={() => addArrayItem("nonTechnicalCertificates", newNonTechnicalCertificate, setNewNonTechnicalCertificate, "")} className="btn-profile btn-profile-accent">Add</button></div>}
              </div>
            </motion.div>
            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header"><Info /> Interests</h3>
              <div className="profile-form-row mb-4">
                <label className="profile-label">Technical Interests</label>
                <textarea name="interestsTechnical" value={profile.interestsTechnical} onChange={handleChange} disabled={!isEditMode} className="profile-textarea" />
              </div>
              <div className="profile-form-row">
                <label className="profile-label">Non-Technical Interests</label>
                <textarea name="interestsNonTechnical" value={profile.interestsNonTechnical} onChange={handleChange} disabled={!isEditMode} className="profile-textarea" />
              </div>
            </motion.div>
          </motion.div>
        );

      case "research":
        return (
          <motion.div variants={cardContainerVariants} initial="hidden" animate="visible" exit={{ opacity: 0, y: -20 }} className="profile-container">
            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header"><FileText /> Research Papers</h3>
              <div className="profile-list">
                {profile.research_papers.map((paper, idx) => (
                  <div key={idx} className="profile-list-item flex justify-between items-center">
                    <a href={paper.url} target="_blank" rel="noreferrer" className="text-blue-500 hover:underline">{paper.title}</a>
                    {isEditMode && <button onClick={() => removeArrayItem("research_papers", idx)} className="text-red-500"><Trash2 size={16} /></button>}
                  </div>
                ))}
                {isEditMode && (
                  <div className="profile-add-row mt-2">
                    <input type="text" placeholder="Title" value={newPaper.title} onChange={e => setNewPaper({ ...newPaper, title: e.target.value })} className="profile-input" />
                    <input type="text" placeholder="URL" value={newPaper.url} onChange={e => setNewPaper({ ...newPaper, url: e.target.value })} className="profile-input" />
                    <button onClick={() => addArrayItem("research_papers", newPaper, setNewPaper, { title: "", url: "" })} className="btn-profile btn-profile-accent">Add</button>
                  </div>
                )}
              </div>
            </motion.div>

            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header"><Globe /> Other Links</h3>
              <div className="profile-form-row mb-4">
                <label className="profile-label">GitHub Username</label>
                {isEditMode ? <input type="text" name="github" value={profile.github} onChange={handleChange} className="profile-input" /> : <div className="profile-input">{profile.github || "Not Provided"}</div>}
              </div>
              <div className="profile-form-row">
                <label className="profile-label">Google Scholar URL</label>
                {isEditMode ? <input type="text" name="google_scholar" value={profile.google_scholar} onChange={handleChange} className="profile-input" /> : <div className="profile-input flex items-center justify-between">{profile.google_scholar || "Not Provided"} {profile.google_scholar && <a href={profile.google_scholar} target="_blank" rel="noreferrer"><ExternalLink size={16} /></a>}</div>}
              </div>
            </motion.div>

            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header"><Briefcase /> Projects</h3>
              <div className="profile-list">
                {profile.projects.map((proj, idx) => (
                  <div key={idx} className="profile-list-item">
                    <div className="flex justify-between">
                      <h4 className="font-bold">{proj.name}</h4>
                      {isEditMode && <button onClick={() => removeArrayItem("projects", idx)} className="text-red-500"><Trash2 size={16} /></button>}
                    </div>
                    <p className="text-sm">{proj.description}</p>
                    {proj.techStack && <p className="text-xs text-gray-500 my-1">Stack: {proj.techStack}</p>}
                    <div className="flex gap-2 text-xs">
                      {proj.liveLink && <a href={proj.liveLink} target="_blank" rel="noreferrer" className="text-blue-500">Live</a>}
                      {proj.repoLink && <a href={proj.repoLink} target="_blank" rel="noreferrer" className="text-blue-500">Repo</a>}
                    </div>
                  </div>
                ))}
                {isEditMode && (
                  <div className="profile-new-item-form profile-form-grid mt-4">
                    <input className="profile-input" placeholder="Project Name" value={newProject.name} onChange={e => setNewProject({ ...newProject, name: e.target.value })} />
                    <textarea className="profile-textarea" placeholder="Description" value={newProject.description} onChange={e => setNewProject({ ...newProject, description: e.target.value })} />
                    <input className="profile-input" placeholder="Tech Stack" value={newProject.techStack} onChange={e => setNewProject({ ...newProject, techStack: e.target.value })} />
                    <input className="profile-input" placeholder="Live Link" value={newProject.liveLink} onChange={e => setNewProject({ ...newProject, liveLink: e.target.value })} />
                    <input className="profile-input" placeholder="Repo Link" value={newProject.repoLink} onChange={e => setNewProject({ ...newProject, repoLink: e.target.value })} />
                    <button onClick={() => addArrayItem("projects", newProject, setNewProject, { name: "", description: "", techStack: "", liveLink: "", repoLink: "" })} className="btn-profile btn-profile-accent full-width">Add Project</button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        );

      case "work":
        return (
          <motion.div variants={cardContainerVariants} initial="hidden" animate="visible" exit={{ opacity: 0, y: -20 }} className="profile-container">
            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header"><Briefcase /> Experience</h3>
              <div className="profile-list">
                {profile.experience.map((exp, idx) => (
                  <div key={idx} className="profile-list-item flex justify-between">
                    <div>
                      <h4 className="font-bold">{exp.companyName} <span className="text-xs bg-gray-200 px-2 py-1 rounded ml-2">{exp.type}</span></h4>
                      <p className="text-sm font-semibold">{exp.role} <span className="text-gray-500 font-normal">| {exp.duration}</span></p>
                      <p className="text-sm mt-1">{exp.description}</p>
                    </div>
                    {isEditMode && <button onClick={() => removeArrayItem("experience", idx)} className="text-red-500"><Trash2 size={16} /></button>}
                  </div>
                ))}
                {isEditMode && (
                  <div className="profile-new-item-form profile-form-grid mt-4">
                    <input className="profile-input" placeholder="Company/Org" value={newExperience.companyName} onChange={e => setNewExperience({ ...newExperience, companyName: e.target.value })} />
                    <input className="profile-input" placeholder="Role" value={newExperience.role} onChange={e => setNewExperience({ ...newExperience, role: e.target.value })} />
                    <input className="profile-input" placeholder="Duration (e.g. 2020-2022)" value={newExperience.duration} onChange={e => setNewExperience({ ...newExperience, duration: e.target.value })} />
                    <select className="profile-select" value={newExperience.type} onChange={e => setNewExperience({ ...newExperience, type: e.target.value })}>
                      <option value="Full-time">Full-time</option><option value="Internship">Internship</option><option value="Freelancing">Freelancing</option><option value="Other">Other</option>
                    </select>
                    <textarea className="profile-textarea" placeholder="Description" value={newExperience.description} onChange={e => setNewExperience({ ...newExperience, description: e.target.value })} />
                    <button onClick={() => addArrayItem("experience", newExperience, setNewExperience, { companyName: "", duration: "", role: "", description: "", type: "Full-time" })} className="btn-profile btn-profile-accent full-width">Add Experience</button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        );

      case "clubs":
        return (
          <motion.div variants={cardContainerVariants} initial="hidden" animate="visible" exit={{ opacity: 0, y: -20 }} className="profile-container">
            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header"><Globe /> Clubs & Activities</h3>
              <div className="profile-list">
                {profile.clubs.map((club, idx) => (
                  <div key={idx} className="profile-list-item flex justify-between">
                    <div>
                      <h4 className="font-bold">{club.clubName}</h4>
                      <p className="text-sm font-semibold">{club.role}</p>
                      <p className="text-sm mt-1">Activities: {club.events}</p>
                    </div>
                    {isEditMode && <button onClick={() => removeArrayItem("clubs", idx)} className="text-red-500"><Trash2 size={16} /></button>}
                  </div>
                ))}
                {isEditMode && (
                  <div className="profile-new-item-form profile-form-grid mt-4">
                    <input className="profile-input" placeholder="Club Name" value={newClub.clubName} onChange={e => setNewClub({ ...newClub, clubName: e.target.value })} />
                    <input className="profile-input" placeholder="Role" value={newClub.role} onChange={e => setNewClub({ ...newClub, role: e.target.value })} />
                    <textarea className="profile-textarea" placeholder="Activities performed" value={newClub.events} onChange={e => setNewClub({ ...newClub, events: e.target.value })} />
                    <button onClick={() => addArrayItem("clubs", newClub, setNewClub, { clubName: "", role: "", events: "" })} className="btn-profile btn-profile-accent full-width">Add Activity</button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        );

      case "connections":
        return (
          <motion.div variants={cardContainerVariants} initial="hidden" animate="visible" exit={{ opacity: 0, y: -20 }} className="profile-container">
            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header"><Users /> Following</h3>
              <div className="profile-list">
                {connectionData.following.length === 0 ? <p className="profile-view-empty">{isViewingOther ? `${profile.name} isn't following anyone yet.` : "You haven't requested to connect with anyone yet."}</p> : (
                  connectionData.following.map(f => (
                    <div key={f.email} className="profile-list-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div onClick={() => navigate(`/${f.email.split('@')[0]}`)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', overflow: 'hidden' }}>
                          {f.profile_pic ? <img src={f.profile_pic} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (f.name || '?').charAt(0)}
                        </div>
                        <div>
                          <div style={{ fontWeight: 'bold' }}>{f.name}</div>
                          <div style={{ fontSize: '0.75rem', color: '#666' }}>{f.status === 'pending' ? 'Request Sent' : 'Connected'}</div>
                        </div>
                      </div>
                      {!isViewingOther && (
                        <button onClick={() => handleRemoveConnection(f.email)} style={{ padding: '4px 8px', background: '#ff6b6b', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.75rem' }}>
                          {f.status === 'pending' ? 'Cancel' : 'Remove'}
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </motion.div>

            <motion.div variants={cardEntranceVariants} className="profile-card">
              <h3 className="profile-card-header"><Users /> Followers</h3>
              <div className="profile-list">
                {connectionData.followers.length === 0 ? <p className="profile-view-empty">{isViewingOther ? `No one has connected with ${profile.name} yet.` : "No one has connected with you yet."}</p> : (
                  connectionData.followers.map(f => (
                    <div key={f.email} className="profile-list-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div onClick={() => navigate(`/${f.email.split('@')[0]}`)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', overflow: 'hidden' }}>
                          {f.profile_pic ? <img src={f.profile_pic} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (f.name || '?').charAt(0)}
                        </div>
                        <div>
                          <div style={{ fontWeight: 'bold' }}>{f.name}</div>
                          <div style={{ fontSize: '0.75rem', color: '#666' }}>{f.status === 'pending' ? 'sent a request' : 'Connection Accepted'}</div>
                        </div>
                      </div>
                      {!isViewingOther && (
                        <div style={{ display: 'flex', gap: 8 }}>
                          {f.status === 'pending' && (
                            <button onClick={() => handleAcceptConnect(f.email)} style={{ padding: '4px 8px', background: '#4ecdc4', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.75rem' }}>
                              Accept
                            </button>
                          )}
                          <button onClick={() => handleRemoveConnection(f.email)} style={{ padding: '4px 8px', background: '#eee', color: '#333', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.75rem' }}>
                            {f.status === 'pending' ? 'Reject' : 'Remove'}
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          </motion.div>
        );
      default: return null;
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={pageEntranceVariants}
      className={`profile-page ${isEditMode ? 'is-editing' : ''}`}
    >
      <motion.header variants={headerVariants} className="profile-header">
        <div className="profile-header-content">
          <h1 className="profile-title">
            <span className="profile-title-text gradient-text">VJ LINK</span>
          </h1>
          <div className="profile-controls">
            {!isViewingOther && (
              <button
                onClick={() => {
                  if (isEditMode) handleSave();
                  else setIsEditMode(true);
                }}
                className={`profile-toggle-btn ${isEditMode ? 'save-mode' : 'view-mode'}`}
              >
                {isEditMode ? <><Save size={18} /> Save Changes</> : <><Edit3 size={18} /> Edit Profile</>}
              </button>
            )}
            <button
              onClick={() => navigate('/home')}
              className="btn-profile btn-profile-secondary"
              style={{ marginLeft: '0.5rem' }}
              title="Go Home"
            >
              <Home size={18} />
            </button>
            <button
              onClick={() => {
                localStorage.removeItem("vjconnect_user");
                navigate("/");
              }}
              className="btn-profile btn-profile-danger"
              style={{ marginLeft: '0.5rem' }}
              title="Logout"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </motion.header>

      <motion.div variants={tabsVariants} className="profile-tabs">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setSelectedTab(tab.id)}
              className={`profile-tab ${selectedTab === tab.id ? 'active' : ''}`}
            >
              <Icon />
              <span className="profile-tab-label">{tab.label}</span>
            </button>
          );
        })}
      </motion.div>

      <AnimatePresence mode="wait">
        {renderTabContent()}
      </AnimatePresence>

      <AnimatePresence>
        {isImageModalOpen && profile.profile_pic && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="profile-image-modal-overlay"
            onClick={() => setIsImageModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="profile-image-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={profile.profile_pic} alt={profile.name} className="profile-full-image" />
              <button className="profile-modal-close" onClick={() => setIsImageModalOpen(false)}><X size={24} /></button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default FacultyProfile;
